/**
 * A growing stock. The reader chooses a kind of unit (LHC, oddělení, dílec, porost, PSK, or the
 * whole area); the map lays out every unit of that kind, and units picked on the map, in the
 * list or in the table are added up: the chart "Dřevní zásoba podle rodu" and the figures show
 * their sum — the whole area while nothing is picked — and each picked unit gets a card.
 * Figures come from the step's `growing_stock.json`, outlines from the `units`, `mls_area` and
 * `field_area` tables of its `units.gpkg`; a bound MLS tree analysis adds its stems and crowns to the map
 * and a section of its own over the stems inside the picked units; bound field annotations add
 * the crew's trunks, and their surveyed areas where the summary itself has none.
 *
 * Crowns are many and their files large (a photo inventory runs to hundreds of MB), so neither
 * crown file is read until something is shown: the whole area, at the level `area` (nothing to
 * pick there, the area is the pick), or a picked unit at the other levels. Only the crowns inside
 * what is shown are drawn. Each file is read once; later picks only filter again.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BarChart } from '@mantine/charts';
import type { Feature, FeatureCollection } from 'geojson';
import { Anchor, Box, Button, Card, Center, Checkbox, CloseButton, Group, Loader, MultiSelect, ScrollArea, SegmentedControl, SimpleGrid, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CHART_AXIS_PROPS } from '@/components/charts';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { useFormat } from '@/components/useFormat';
import { FIELD_TABLES, sourceId, UNIT_ID, UNIT_PARTS, type LayerOptions, type LayerRef, type TiledSource } from '@/map/layers';
import { PMTILES_LAYERS, pmtilesTileUrl, type Archive } from '@/map/pmtiles';
import { setCrownTileMask } from '@/map/tileMask';
import { FeaturePopup, type FeatureTarget } from '@/map/FeaturePopup';
import type { FeatureTitle } from '@/map/featureTitle';
import { MapView, type RelatedFeature } from '@/map/MapView';
import { speciesColor } from '@/theme/colors';
import { brand, forest } from '@/theme/theme';
import { GROWING_SECTIONS, growingStockConfig, LEVELS, type Level } from './growing/config';
import {
  basalBars,
  basalColumns,
  canopyShares,
  genusKey,
  hierarchy,
  MLS_AREA_TABLE,
  FIELD_AREA_TABLE,
  shownMethods,
  stackBars,
  sumUnits,
  unitsBounds,
  unitBasal,
  unitTotal,
  UNITS_FILE,
  useGrowingStockData,
  type GrowingStock,
  type Hierarchy,
  type OverlapStand,
  type StackBar,
  type StandPlan,
  type Unit,
} from './growing/data';
import { crownsOf, CROWN_TABLE, mlsIndex, SIDEVIEW_PROP, type MlsIndex, mlsRelated, mlsStats, pointsIn, SECTION_PROP, STEM_TABLE, stems, useMlsData, type MlsStats } from './growing/mls';
import { MapFrame } from './MapFrame';
import type { KindReportProps } from './ReportPage';
import { Section } from './trees/Section';
import { useTreesData } from './trees/useTreesData';
import { useArchive, useArchiveLayer } from './growing/archive';
import { useFieldData } from './growing/field';

type Mode = 'abs' | 'perHa';

/** The id the sum of the picked units goes by. */
const SELECTION_ID = '__picked';
const LEVEL_ID = '__level';

/** The step's names, in the reader's language where the interface has its own. */
function useNames(gs: GrowingStock | null) {
  const { t, i18n } = useTranslation('reports');
  const czech = i18n.language.startsWith('cs');
  const level = (l: string) => t(`growing.levels.${l}`, { defaultValue: gs?.levels[l] ?? l });
  return {
    method: (m: string) => t(`growing.methods.${m}`, { defaultValue: gs?.methods[m] ?? gs?.basalMethods[m] ?? m }),
    level,
    // The step names genera in Czech; another language falls back to the Latin.
    genus: (g: string) => t(`species.${genusKey(g)}`, { defaultValue: (czech && gs?.genera[g]) || genusKey(g) }),
    unit: (u: Unit) => (u.level === 'area' ? level('area') : `${level(u.level)} ${u.name}`),
  };
}
type Names = ReturnType<typeof useNames>;
/** Volume method → the G methods shown beside it (basalColumns). */
type BasalCols = Record<string, string[]>;

/** Where a unit sits, without the area and without itself: `101703 › 1 › 1A › 1Aa`. */
const parentPath = (h: Hierarchy, u: Unit) =>
  h
    .path(u.id)
    .filter((x) => x !== u && x.level !== 'area')
    .map((x) => x.name)
    .join(' › ');

/** A tile version for a set of picks: the same picks, the same tiles from the browser's cache. */
function versionOf(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (Math.imul(h, 31) + key.charCodeAt(i)) | 0;
  return h >>> 0;
}

/** The one MapLibre source an archive's layers share. */
const archiveSource = (object: string) => `${object}:pmtiles`;

const byName = (a: Unit, b: Unit) => a.name.localeCompare(b.name, 'cs', { numeric: true });
const idOf = (f: Feature) => String(f.properties?.[UNIT_ID] ?? '');

export function GrowingStockReport({ refs, inputs = [], linkSource, options, onOptionsChange, tokenSource, config, onVectorError, onVectorProgress }: KindReportProps) {
  const { t } = useTranslation('reports');
  const cfg = useMemo(() => growingStockConfig(config), [config]);
  const s = cfg.sections;
  const object = inputs.find((i) => i.role === 'forest_summary' && i.kind === 'forest_summary' && i.available)?.object;
  const mlsObject = inputs.find((i) => i.role === 'mls_trees' && i.available)?.object;
  const showMls = s.mls.enabled && !!mlsObject;
  const treesObject = inputs.find((i) => i.role === 'trees' && i.available)?.object;
  // The field crew's trunks: one small GeoPackage, read when the page opens.
  const fieldObject = inputs.find((i) => i.role === 'field' && i.available)?.object;
  const fieldGrant = refs.find((r) => r.object === fieldObject && r.via === 'link');
  const fieldFile = useMemo(() => (fieldObject && fieldGrant ? { object: fieldObject, path: fieldGrant.path, digest: fieldGrant.digest } : undefined), [fieldObject, fieldGrant]);
  const field = useFieldData(fieldFile, linkSource, s.map.enabled);
  // The units' file waits for the grant (which names its digest), so it is read once, cached.
  const data = useGrowingStockData(object, linkSource, s.map.enabled || showMls, refs.find((r) => r.object === object)?.digest);
  // The scan's own GeoPackage, named in the report's grant, and its digest for the cache.
  const mlsGrant = refs.find((r) => r.object === mlsObject);
  const mlsFile = useMemo(() => (mlsObject ? { object: mlsObject, path: mlsGrant?.path ?? '', digest: mlsGrant?.digest ?? '' } : undefined), [mlsObject, mlsGrant?.path, mlsGrant?.digest]);
  // Set by the first pick (or by leaving the whole area) and kept: the crown files are read then, once.
  const [everPicked, setEverPicked] = useState(false);
  const gs = data.data;
  const names = useNames(gs);
  const h = useMemo(() => (gs ? hierarchy(gs.units) : null), [gs]);
  const methods = useMemo(() => (gs ? shownMethods(gs, cfg.methods) : []), [gs, cfg.methods]);
  const basal = useMemo(() => (gs ? basalColumns(gs, methods) : {}), [gs, methods]);
  const basalMethods = useMemo(() => Object.values(basal).flat(), [basal]);
  const levels = useMemo(() => LEVELS.filter((l) => gs?.units.some((u) => u.level === l)), [gs]);
  const [chosen, setChosen] = useState<Level | null>(null);
  const level: Level = chosen ?? (levels.includes(cfg.level) ? cfg.level : (levels[0] ?? 'area'));
  // At the level `area` nothing can be picked: the whole area is what the map shows, crowns and all.
  const wholeShown = !!gs && level === 'area';
  // Without a pick the chosen kind of unit shows all its units, crowns and all, as the whole area does.
  const crownsWanted = everPicked || (!!gs && levels.length > 0);
  // The photo crowns, read as the trees report reads them — by the report, not the map — once,
  // as soon as a kind of unit is shown: their tile server exists before the map asks it for a tile.
  const photoRef = useMemo(() => refs.find((r) => r.object === treesObject && r.via === 'link'), [refs, treesObject]);
  // A tile archive beside a crown GeoPackage spares the page that file altogether: the map draws
  // from the archive, and the scan's stems are decoded from its coarsest tiles. Looked for with
  // the first pick or the whole area, like everything about the crowns.
  const photoArchive = useArchive(treesObject, photoRef?.path, linkSource, crownsWanted && s.map.enabled);
  // The scan's stems and crowns are drawn over every unit, picked or not, so they are read with the page.
  const mlsArchive = useArchive(mlsObject, mlsFile?.path || undefined, linkSource, s.map.enabled || (crownsWanted && showMls));
  const photoRefs = useMemo(() => (photoArchive.state === 'none' && photoRef ? [photoRef] : []), [photoArchive.state, photoRef]);
  const photo = useTreesData(photoRefs, linkSource, onVectorError, onVectorProgress);
  const mlsProgress = useCallback((p: Parameters<NonNullable<KindReportProps['onVectorProgress']>>[1]) => mlsObject && onVectorProgress?.(mlsObject, p), [mlsObject, onVectorProgress]);
  const archiveStems = useArchiveLayer(mlsArchive.archive, PMTILES_LAYERS.mls.points);
  const gpkgMls = useMlsData(mlsArchive.state === 'none' ? mlsFile : undefined, linkSource, s.map.enabled, mlsProgress);
  // The stems as the page counts them, wherever they came from; the crowns only from the GeoPackage.
  const mls = mlsArchive.state === 'ready' ? { stems: archiveStems.fc, crowns: null, loading: archiveStems.loading, failed: archiveStems.failed } : mlsArchive.state === 'none' ? gpkgMls : { stems: null, crowns: null, loading: mlsArchive.state === 'checking', failed: false };
  // Units picked to be added up; page state, and only ever of the chosen kind.
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<Mode>(cfg.perHa ? 'perHa' : 'abs');
  const [focus, setFocus] = useState<[number, number, number, number] | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const ofLevel = useMemo(() => (gs ? gs.units.filter((u) => u.level === level).sort(byName) : []), [gs, level]);
  const pickedUnits = useMemo(() => (h ? picked.flatMap((id) => (h.byId.has(id) ? [h.byId.get(id)!] : [])) : []), [h, picked]);
  // Without an `area` unit, the plan's roots make the whole; MLS plots lie inside it, not beside it.
  const whole = useMemo(() => (h ? (h.roots.find((u) => u.level === 'area') ?? sumUnits(h.roots.filter((u) => u.level !== 'mls' && u.level !== 'field'), 'area', '')) : null), [h]);
  // Without a pick, a kind of unit other than the whole area stands for all its units together.
  const levelAll = useMemo(() => (level !== 'area' && ofLevel.length ? sumUnits(ofLevel, LEVEL_ID, ofLevel.map((u) => u.name).join(', ')) : null), [level, ofLevel]);
  const subject = useMemo(() => (pickedUnits.length ? sumUnits(pickedUnits, SELECTION_ID, pickedUnits.map((u) => u.name).join(', ')) : (levelAll ?? whole)), [pickedUnits, levelAll, whole]);
  const levelFeatures = useMemo(() => (data.units ? data.units.features.filter((f) => f.properties?.level === level) : null), [data.units, level]);
  const pickedFeatures = useMemo(() => (levelFeatures ? levelFeatures.filter((f) => picked.includes(idOf(f))) : null), [levelFeatures, picked]);
  // What the map shows crowns in: the picked units, or else every unit of the chosen kind.
  const showing = wholeShown || picked.length > 0 || !!levelFeatures?.length;
  const shownFeatures = picked.length ? pickedFeatures : levelFeatures;
  const shownIds = useMemo(() => (picked.length ? picked : (levelFeatures ?? []).map(idOf)), [levelFeatures, picked]);

  // The MLS stems standing inside what is shown, for the MLS section; nothing while nothing is. The
  // whole area without an outline of its own takes every stem.
  const pickedStems = useMemo(() => {
    if (!mls.stems) return null;
    if (shownFeatures?.length) return pointsIn(mls.stems, shownFeatures);
    return wholeShown ? mls.stems.features : null;
  }, [mls.stems, shownFeatures, wholeShown]);
  // On the map the scan stands in every unit of the chosen kind, picked or not.
  const levelStems = useMemo(() => {
    if (!mls.stems) return null;
    return levelFeatures?.length ? pointsIn(mls.stems, levelFeatures) : mls.stems.features;
  }, [mls.stems, levelFeatures]);
  const stats = useMemo(() => (pickedStems ? mlsStats(stems({ type: 'FeatureCollection', features: pickedStems })) : null), [pickedStems]);
  // The photo crowns are tiles cut in a worker, all of them; only tiles touching a picked unit are
  // asked for (tileMask.ts), and a new pick refetches them under a new version.
  const crownVersion = useMemo(() => versionOf(shownIds.join('|')), [shownIds]);
  useLayoutEffect(() => {
    // The photo crowns' tiles touch what is shown; the scan's touch every unit of the chosen kind.
    // The whole area without an outline of its own is no mask at all.
    const shapesOf = (fs: Feature[] | null) => (fs?.length ? fs.flatMap((f) => (f.geometry ? [f.geometry] : [])) : null);
    const masks: [string | undefined, ReturnType<typeof shapesOf>][] = [
      [treesObject, shapesOf(shownFeatures)],
      [mlsObject, shapesOf(levelFeatures)],
    ];
    for (const [o, shapes] of masks) if (o) setCrownTileMask(o, shapes);
    return () => {
      for (const [o] of masks) if (o) setCrownTileMask(o, null);
    };
  }, [treesObject, mlsObject, shownFeatures, levelFeatures]);
  const levelVersion = useMemo(() => versionOf((levelFeatures ?? []).map(idOf).join('|')), [levelFeatures]);
  const index = useMemo(() => mlsIndex(mls.stems, mls.crowns), [mls.stems, mls.crowns]);
  // A clicked MLS crown is joined to its stem on the map, a clicked stem to its crown.
  const relate = useCallback(
    ({ objectId, properties }: { objectId: string; properties: Record<string, unknown> }): RelatedFeature | null => {
      if (!mlsObject) return null;
      const clicked = objectId === `${mlsObject}:${STEM_TABLE}` ? 'stem' : objectId === `${mlsObject}:${CROWN_TABLE}` ? 'crown' : null;
      if (!clicked) return null;
      const other = `${mlsObject}:${clicked === 'stem' ? CROWN_TABLE : STEM_TABLE}`;
      if (mlsArchive.state === 'ready') {
        // In the archive a stem and its crown share their id (the tree's): the map finds the
        // counterpart in its tiles and draws the line itself.
        const tree = properties[clicked === 'stem' ? 'id' : 'tree_id'];
        if (typeof tree !== 'number' && typeof tree !== 'string') return null;
        return { object: other, id: tree, source: sourceId(archiveSource(mlsObject)), sourceLayer: PMTILES_LAYERS.mls[clicked === 'stem' ? 'polygons' : 'points'] };
      }
      const found = mlsRelated(index, clicked, properties);
      if (!found) return null;
      return { object: other, id: found.feature.id!, line: found.line };
    },
    [mlsObject, index, mlsArchive.state],
  );

  // One object per content, so the map hands MapLibre only what a pick really changed.
  const asFc = (features: Feature[] | null): FeatureCollection | null => (features ? { type: 'FeatureCollection', features } : null);
  const unitsFc = useMemo(() => asFc(levelFeatures), [levelFeatures]);
  const dimFc = useMemo(() => asFc(levelFeatures?.filter((f) => !shownIds.includes(idOf(f))) ?? null), [levelFeatures, shownIds]);
  // On the map: the scan's stems and crowns in every unit of the chosen kind; the picked ones feed the MLS section.
  const allStemsFc = useMemo(() => asFc(levelStems), [levelStems]);
  const allMlsCrownsFc = useMemo(() => (mls.crowns && levelStems ? asFc(crownsOf(mls.crowns, levelStems)) : null), [mls.crowns, levelStems]);

  const layers = useMemo(() => {
    if (!object) return { refs: [] as LayerRef[], vectors: {} as Record<string, FeatureCollection>, external: [] as string[] };
    const ortho = inputs.find((i) => i.role === 'ortho' && i.available)?.object;
    // The photo crowns join the map with a pick: from their archive, or once their tile server exists.
    const tiled = (o: string, archive: Archive, layers: TiledSource['layers'], promoteId: Record<string, string>, version = crownVersion): TiledSource => ({
      url: pmtilesTileUrl(o, version),
      source: archiveSource(o),
      maxzoom: archive.header.maxZoom,
      layers,
      promoteId,
    });
    const photoTiles = photoArchive.state === 'ready' && treesObject ? tiled(treesObject, photoArchive.archive, PMTILES_LAYERS.photo, { [PMTILES_LAYERS.photo.points]: 'id', [PMTILES_LAYERS.photo.polygons]: 'id' }) : undefined;
    const mlsTiles = mlsArchive.state === 'ready' && mlsObject ? tiled(mlsObject, mlsArchive.archive, PMTILES_LAYERS.mls, { [PMTILES_LAYERS.mls.points]: 'id', [PMTILES_LAYERS.mls.polygons]: 'tree_id' }, levelVersion) : undefined;
    const crowns = showing && photoRef && (photoTiles || photo.trees) ? photoRef : undefined;
    // Layers drawn from an archive are no one's to load: the map is told so.
    const fromArchive: string[] = [];
    const archived = (ref: LayerRef, tiles: TiledSource): LayerRef[] => {
      fromArchive.push(ref.object);
      return [{ ...ref, tiles }];
    };
    const raster = refs.find((r) => r.object === ortho && r.via === 'tiler');
    const digest = refs.find((r) => r.object === object)?.digest ?? '';
    const vectors: Record<string, FeatureCollection> = {};
    const own = (ref: LayerRef, fc: FeatureCollection | null): LayerRef[] => {
      if (!fc) return [];
      vectors[ref.object] = fc;
      return [ref];
    };
    const unitRef = (table?: string): LayerRef => ({ object: table && table !== UNIT_PARTS.hit ? `${object}:${table}` : object, object_kind: 'forest_summary', via: 'link', path: UNITS_FILE, digest, ...(table ? { table } : {}) });
    const fieldRef = (table: string): LayerRef => ({ object: `${fieldObject}:${table}`, object_kind: 'field_annotations', via: 'link', path: fieldGrant?.path ?? '', digest: fieldGrant?.digest ?? '', table });
    const mlsRef = (table: string): LayerRef => ({ object: `${mlsObject}:${table}`, object_kind: 'mls_tree_analysis', via: 'link', path: mlsFile?.path ?? '', digest: '', table });
    // With a pick the crowns come in: the units split around them, their outlines and a veil over
    // the unpicked ones above, what a click hits below. Crown tiles reach past the picks; the veil fades that.
    const split = showing;
    // Top first: the stems, the field trunks, the units' outlines, the veil, the photo crowns, the scan's crowns, the units, the scan's and the survey's outlines, the orthophoto.
    const ordered = [
      ...(mlsObject ? (mlsTiles ? archived(mlsRef(STEM_TABLE), mlsTiles) : own(mlsRef(STEM_TABLE), allStemsFc)) : []),
      ...(fieldObject ? own(fieldRef(FIELD_TABLES.trunk), field.trunks) : []),
      ...(split ? own(unitRef(UNIT_PARTS.outline), unitsFc) : []),
      ...(split ? own(unitRef(UNIT_PARTS.dim), dimFc) : []),
      ...(crowns ? (photoTiles ? archived(crowns, photoTiles) : photo.trees ? own(crowns, photo.trees) : []) : []),
      ...(mlsObject ? (mlsTiles ? archived(mlsRef(CROWN_TABLE), mlsTiles) : own(mlsRef(CROWN_TABLE), allMlsCrownsFc)) : []),
      ...own(unitRef(split ? UNIT_PARTS.hit : undefined), unitsFc),
      ...own({ object: `${object}:${MLS_AREA_TABLE}`, object_kind: 'forest_summary', via: 'link', path: UNITS_FILE, digest, table: MLS_AREA_TABLE }, data.mlsArea),
      ...own({ object: `${object}:${FIELD_AREA_TABLE}`, object_kind: 'forest_summary', via: 'link', path: UNITS_FILE, digest, table: FIELD_AREA_TABLE }, data.fieldArea),
      // The same areas as the summary's own, unclipped: drawn only where the summary has none.
      ...(fieldObject && !data.fieldArea ? own(fieldRef(FIELD_TABLES.area), field.areas) : []),
      ...(raster ? [raster] : []),
    ];
    return { refs: ordered, vectors, external: [...Object.keys(vectors), ...fromArchive] };
  }, [object, mlsObject, treesObject, mlsFile?.path, showing, inputs, refs, photoRef, photo.trees, photoArchive, mlsArchive, crownVersion, levelVersion, allStemsFc, allMlsCrownsFc, unitsFc, dimFc, data.mlsArea, data.fieldArea, fieldObject, fieldGrant, field.trunks, field.areas]);

  if (!GROWING_SECTIONS.some((k) => s[k].enabled)) return <EmptyState title={t('growing.nothingEnabled')} />;
  if (data.loading && object) {
    return (
      <Center py="md">
        <Loader color="forest" />
      </Center>
    );
  }
  if (!object || data.failed || !gs || !h || !subject) return <EmptyState title={t('growing.unreadable')} />;

  // The picks are the page's, not part of the view a viewer's browser keeps.
  const change = (next: LayerOptions) => {
    const rest = { ...next };
    delete rest.selectedUnits;
    delete rest.crownVersion;
    onOptionsChange(rest);
  };
  const choose = (next: Level) => {
    // The whole area has shown its crowns: they stay read for the picks to come.
    if (wholeShown) setEverPicked(true);
    setChosen(next);
    setPicked([]);
  };
  const pick = (ids: string[]) => {
    setPicked(ids);
    if (ids.length) setEverPicked(true);
  };
  const toggle = (id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    // Only a pick can be undone, so any toggle means something has been picked.
    setEverPicked(true);
  };
  const pickable = level !== 'area';
  const perHa = mode === 'perHa';
  const pickedNames = pickedUnits.map((u) => u.name).join(', ');
  const zoom = () => pickedFeatures && setFocus(unitsBounds({ type: 'FeatureCollection', features: pickedFeatures }, new Set(picked), (p) => String(p[UNIT_ID] ?? '')));
  // A card's unit, on the map: the map comes into view too, since the cards stand above it.
  const goTo = (id: string) => {
    const box = pickedFeatures && unitsBounds({ type: 'FeatureCollection', features: pickedFeatures }, new Set([id]), (p) => String(p[UNIT_ID] ?? ''));
    if (!box) return;
    setFocus(box);
    mapRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };
  const scope = pickedUnits.length ? t('growing.picked.of', { level: names.level(level), names: pickedNames }) : levelAll ? t('growing.allOfLevel', { level: names.level(level), count: ofLevel.length }) : t('growing.wholeArea');

  return (
    <Stack gap="xl">
      <Group align="flex-end" gap="md" wrap="wrap" className="report-actions">
        {levels.length > 1 && (
          <Stack gap={4}>
            <Text fz={14} fw={500}>
              {t('growing.level')}
            </Text>
            <SegmentedControl value={level} onChange={(v) => choose(v as Level)} data={levels.map((l) => ({ value: l, label: names.level(l) }))} data-testid="gs-level" />
          </Stack>
        )}
        {pickable && <UnitPicker units={ofLevel} h={h} level={names.level(level)} picked={picked} onChange={pick} onZoom={s.map.enabled && pickedFeatures?.length ? zoom : undefined} />}
      </Group>
      {s.summary.enabled && <SummarySection unit={subject} methods={methods} basal={basal} names={names} title={`${t('growing.sections.summary')}: ${scope}`} />}
      {s.chart.enabled && (
        <Section title={`${t('growing.sections.chart')}: ${scope}`}>
          <Stack gap="sm">
            <ModeSwitch mode={mode} onChange={setMode} />
            <StackChart bars={stackBars(subject, methods, perHa)} names={names} unit={perHa ? t('growing.units.m3ha') : t('growing.units.m3')} />
          </Stack>
        </Section>
      )}
      {s.chart.enabled && basalBars(subject, basalMethods, perHa).length > 0 && (
        <Section title={`${t('growing.sections.basalChart')}: ${scope}`}>
          <Box data-testid="gs-basal-chart">
            <StackChart bars={basalBars(subject, basalMethods, perHa)} names={names} unit={perHa ? t('growing.units.m2ha') : t('growing.units.m2')} legend />
          </Box>
        </Section>
      )}
      {pickedUnits.length > 0 && <PickedSection units={pickedUnits} h={h} methods={methods} basal={basal} perHa={perHa} names={names} onRemove={toggle} onGoTo={s.map.enabled && !data.unitsFailed && pickedFeatures?.length ? goTo : undefined} />}
      {s.map.enabled && (
        <Section title={t('growing.sections.map', { level: names.level(level) })}>
          {data.unitsFailed ? (
            <Text c={brand.textMuted} data-testid="gs-units-unreadable">
              {t('growing.unitsUnreadable')}
            </Text>
          ) : (
            <Box ref={mapRef}>
              <MapFrame testId="report-growing_stock" wide={!!options.wide} onWideChange={(wide) => change({ ...options, wide })}>
                <MapView
                  refs={layers.refs}
                  linkSource={linkSource}
                  externalVectors={layers.external}
                  vectors={layers.vectors}
                  focus={focus}
                  relate={relate}
                  // A unit opens its own popup, from growing_stock.json, which is where it is picked from the map.
                  renderPopup={(target, close, title) => {
                    const unit = target.objectId === object ? h.byId.get(String(target.properties[UNIT_ID] ?? '')) : undefined;
                    if (unit) return <UnitPopup unit={unit} h={h} methods={methods} basalMethods={basalMethods} names={names} perHa={perHa} picked={pickable ? picked.includes(unit.id) : null} onToggle={() => toggle(unit.id)} onClose={close} />;
                    // An MLS tree shows its side view and stem cross-section under its attributes, when it has them.
                    const view = mlsObject ? treeImageOf(index, target.objectId, mlsObject, target.properties, SIDEVIEW_PROP) : null;
                    const section = mlsObject ? treeImageOf(index, target.objectId, mlsObject, target.properties, SECTION_PROP) : null;
                    if (!view && !section) return null;
                    return <MlsTreePopup target={target} close={close} title={title} object={mlsObject!} view={view} section={section} linkSource={linkSource} />;
                  }}
                  onVectorError={onVectorError}
                  onVectorProgress={onVectorProgress}
                  options={{ colorBy: 'species', ...options, selectedUnits: shownIds, crownVersion }}
                  onOptionsChange={change}
                  tokenSource={tokenSource}
                />
              </MapFrame>
            </Box>
          )}
        </Section>
      )}
      {s.units.enabled && pickable && (
        <Section title={t('growing.sections.units', { level: names.level(level) })}>
          <Stack gap="sm">
            {(!s.chart.enabled || (s.map.enabled && picked.length > 0 && !!pickedFeatures?.length)) && (
              <Group gap="sm" className="report-actions">
                {!s.chart.enabled && <ModeSwitch mode={mode} onChange={setMode} />}
                {s.map.enabled && picked.length > 0 && !!pickedFeatures?.length && (
                  <Button variant="default" size="xs" onClick={zoom} data-testid="gs-zoom-table">
                    {t('growing.picked.zoom')}
                  </Button>
                )}
              </Group>
            )}
            <UnitsTable units={ofLevel} h={h} methods={methods} basal={basal} perHa={perHa} names={names} picked={picked} onToggle={toggle} />
          </Stack>
        </Section>
      )}
      {s.canopy.enabled && <CanopySection whole={whole} units={level === 'area' ? [] : ofLevel} level={names.level(level)} byGenus={s.canopy.byGenus} names={names} />}
      {showMls && <MlsSection picked={showing} stats={stats} loading={mls.loading} failed={mls.failed || (picked.length > 0 && !pickedFeatures)} scope={scope} names={names} />}
      {s.plan.enabled && <PlanSection gs={gs} />}
    </Stack>
  );
}

/** The units of the chosen kind to add up: a searchable list by name with where each lies, a zoom and a way to drop them all. */
function UnitPicker({ units, h, level, picked, onChange, onZoom }: { units: Unit[]; h: Hierarchy; level: string; picked: string[]; onChange: (ids: string[]) => void; onZoom?: () => void }) {
  const { t } = useTranslation('reports');
  const data = useMemo(
    () =>
      units.map((u) => {
        const path = parentPath(h, u);
        return { value: u.id, label: path ? `${u.name} (${path})` : u.name };
      }),
    [units, h],
  );
  return (
    <Group align="flex-end" gap="sm" wrap="wrap" style={{ flex: 1 }}>
      <MultiSelect
        label={t('growing.picked.label', { level })}
        placeholder={picked.length ? undefined : t('growing.picked.search')}
        searchable
        data={data}
        value={picked}
        onChange={onChange}
        style={{ flex: 1, minWidth: 260, maxWidth: 640 }}
        data-testid="gs-picker"
      />
      {picked.length > 0 && (
        <>
          {onZoom && (
            <Button variant="default" onClick={onZoom} data-testid="gs-zoom">
              {t('growing.picked.zoom')}
            </Button>
          )}
          <Button variant="subtle" onClick={() => onChange([])} data-testid="gs-clear">
            {t('growing.picked.clear')}
          </Button>
        </>
      )}
    </Group>
  );
}

/**
 * An image of a clicked MLS stem or crown (`prop`: its side view or its stem cross-section): its
 * own path, or for a crown its stem's. The path is relative to the analysis's GeoPackage; the
 * backend resolves it there.
 */
function treeImageOf(index: MlsIndex, objectId: string, mlsObject: string, properties: Record<string, unknown>, prop: string): string | null {
  const own = properties[prop];
  if (typeof own === 'string' && own) return own;
  if (objectId !== `${mlsObject}:${CROWN_TABLE}`) return null;
  const stem = index.stems.get(String(properties.tree_id ?? ''));
  const theirs = stem?.properties?.[prop];
  return typeof theirs === 'string' && theirs ? theirs : null;
}

/** The popup is this wide when it shows a tree's side view and cross-section side by side. */
const TREE_IMAGES_WIDTH = 560;

/** A link to one of a tree's images, signed like any file of the analysis; `failed` once it is known not to be there or not to load. */
function useTreeImage(object: string, path: string | null, linkSource: KindReportProps['linkSource']) {
  const [state, setState] = useState<{ path: string | null; url: string | null; failed: boolean }>({ path: null, url: null, failed: false });
  useEffect(() => {
    if (!path) return;
    let alive = true;
    linkSource.url(object, path).then(
      (u) => alive && setState({ path, url: u, failed: false }),
      () => alive && setState({ path, url: null, failed: true }),
    );
    return () => {
      alive = false;
    };
  }, [object, path, linkSource]);
  const current = state.path === path ? state : { path, url: null, failed: false };
  return { url: current.failed ? null : current.url, fail: () => setState({ path, url: null, failed: true }) };
}

/**
 * A clicked MLS tree's attributes with its side view and its stem cross-section, side by side in a
 * wider card when it has both, each alone across the card's width otherwise. An image that is not
 * there or will not load leaves nothing behind, and the card narrows back.
 */
function MlsTreePopup({ target, close, title, object, view, section, linkSource }: { target: FeatureTarget; close: () => void; title: FeatureTitle; object: string; view: string | null; section: string | null; linkSource: KindReportProps['linkSource'] }) {
  const side = useTreeImage(object, view, linkSource);
  const cut = useTreeImage(object, section, linkSource);
  const both = !!side.url && !!cut.url;
  const extra =
    side.url || cut.url ? (
      <Group gap={8} align="flex-start" wrap="nowrap" mt={6}>
        {side.url && <TreeImage url={side.url} kind="sideview" ratio={2} onError={side.fail} />}
        {cut.url && <TreeImage url={cut.url} kind="section" ratio={1} onError={cut.fail} />}
      </Group>
    ) : null;
  return <FeaturePopup target={target} onClose={close} title={title} extra={extra} width={both ? TREE_IMAGES_WIDTH : undefined} />;
}

/**
 * One of a tree's images, opening full size. Beside another it takes a share of the row by its
 * aspect ratio (`ratio` until it has loaded), so the two stand equally tall. The cross-section
 * carries its own DBH, so it gets only a caption.
 */
function TreeImage({ url, kind, ratio, onError }: { url: string; kind: 'sideview' | 'section'; ratio: number; onError: () => void }) {
  const { t } = useTranslation('reports');
  const [aspect, setAspect] = useState(ratio);
  return (
    <Box style={{ flex: `${aspect} 1 0`, minWidth: 0 }}>
      <Anchor href={url} target="_blank" rel="noreferrer" aria-label={t(`growing.${kind}.open`)} data-testid={`gs-${kind}`}>
        <img
          src={url}
          alt={t(`growing.${kind}.alt`)}
          loading="lazy"
          style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 6 }}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w > 0 && h > 0) setAspect(w / h);
          }}
          onError={onError}
        />
      </Anchor>
      {kind === 'section' && (
        <Text fz={11} c={brand.textMuted} mt={2}>
          {t('growing.section.caption')}
        </Text>
      )}
    </Box>
  );
}

/**
 * A clicked unit as growing_stock.json knows it: where it lies, its areas, its stock by the
 * report's methods and its leading genera, a PSK's plan record or an MLS plot's PSKs, and the
 * button that picks it. `picked` is null where units cannot be picked (the whole area).
 */
function UnitPopup({
  unit,
  h,
  methods,
  basalMethods,
  names,
  perHa,
  picked,
  onToggle,
  onClose,
}: {
  unit: Unit;
  h: Hierarchy;
  methods: string[];
  basalMethods: string[];
  names: Names;
  perHa: boolean;
  picked: boolean | null;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const ha = t('growing.units.ha');
  const path = parentPath(h, unit);
  const rows = (pairs: [string, string | null][]) =>
    pairs
      .filter((r): r is [string, string] => r[1] !== null)
      .map(([label, text]) => (
        <Group key={label} justify="space-between" gap="xs" wrap="nowrap">
          <Text fz={12} c={brand.textMuted}>
            {label}
          </Text>
          <Text fz={12} fw={600} ta="right">
            {text}
          </Text>
        </Group>
      ));
  return (
    <Card padding="sm" radius="lg" shadow="md" w={300} maw="100%" data-testid="gs-unit-popup">
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={0}>
            <Text fw={700} fz={14}>
              {names.unit(unit)}
            </Text>
            {path && (
              <Text fz={11} c={brand.textMuted}>
                {path}
              </Text>
            )}
          </Stack>
          <CloseButton size="sm" aria-label={t('growing.popup.close')} onClick={onClose} />
        </Group>
        <Text fz={12} c={brand.textMuted} data-testid="gs-popup-areas">
          {[
            unit.areaHa === null ? null : `${number(unit.areaHa, 2)} ${ha}`,
            unit.orthoCoverage === null ? null : t('growing.popup.covered', { value: number(unit.orthoCoverage * 100) }),
            unit.mlsHa ? t('growing.popup.mls', { value: number(unit.mlsHa, 2) }) : null,
            unit.fieldHa ? t('growing.popup.field', { value: number(unit.fieldHa, 2) }) : null,
            unit.canopy?.cover == null ? null : t('growing.popup.canopy', { value: number(unit.canopy.cover * 100) }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {/* The report's own chart, small: a bar per method, by genus, the totals at the ends. */}
        <Box data-testid="gs-popup-chart">
          <StackChart bars={stackBars(unit, methods, perHa)} names={names} unit={perHa ? t('growing.units.m3ha') : t('growing.units.m3')} compact legend />
        </Box>
        {basalMethods.some((m) => unitBasal(unit, m) !== null) && (
          <Stack gap={2} data-testid="gs-popup-basal">
            <Text fz={11} c={brand.textMuted}>
              {t('growing.basal.title')}
            </Text>
            {rows(basalMethods.map((m): [string, string | null] => [names.method(m), unitBasal(unit, m) === null ? null : `${number(unitBasal(unit, m)!, 1)} ${t('growing.units.m2ha')}`]))}
          </Stack>
        )}
        {unit.plan && (
          <Stack gap={2} data-testid="gs-popup-plan">
            {rows([
              [t('growing.stand.age'), unit.plan.age === null ? null : number(unit.plan.age)],
              [t('growing.stand.stocking'), unit.plan.stocking === null ? null : number(unit.plan.stocking, 1)],
              [t('growing.stand.forestType'), unit.plan.forestType],
              [t('growing.stand.composition'), unit.plan.composition],
            ])}
          </Stack>
        )}
        {unit.stands.length > 0 && (
          <Stack gap={2} data-testid="gs-popup-stands">
            <Text fz={11} c={brand.textMuted}>
              {t('growing.overlap.stand')}
            </Text>
            {rows(unit.stands.map((st): [string, string | null] => [st.name, st.share === null ? '—' : `${number(st.share * 100)} %`]))}
          </Stack>
        )}
        {picked !== null && (
          <Button
            size="xs"
            variant={picked ? 'default' : 'filled'}
            onClick={() => {
              // The pick shows on the map and in the cards; the popup has done its job.
              onToggle();
              onClose();
            }}
            data-testid="gs-popup-toggle"
          >
            {picked ? t('growing.popup.remove') : t('growing.popup.add')}
          </Button>
        )}
      </Stack>
    </Card>
  );
}

function SummarySection({ unit, methods, basal, names, title }: { unit: Unit; methods: string[]; basal: BasalCols; names: Names; title: string }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const ha = t('growing.units.ha');
  const areas = [
    unit.areaHa !== null && <StatCard key="area" label={t('growing.kpi.area')} value={number(unit.areaHa, 2)} unit={ha} accent testId="gs-stat-area" />,
    unit.coveredHa !== null && (
      <StatCard
        key="covered"
        label={t('growing.kpi.covered')}
        value={number(unit.coveredHa, 2)}
        unit={ha}
        sub={unit.orthoCoverage === null ? undefined : t('growing.kpi.coveredSub', { value: number(unit.orthoCoverage * 100) })}
      />
    ),
    !!unit.mlsHa && <StatCard key="mls" label={t('growing.kpi.mls')} value={number(unit.mlsHa, 2)} unit={ha} />,
    !!unit.fieldHa && <StatCard key="field" label={t('growing.kpi.field')} value={number(unit.fieldHa, 2)} unit={ha} testId="gs-stat-field-ha" />,
    unit.canopy && unit.canopy.cover !== null && (
      <StatCard key="canopy" label={t('growing.kpi.canopy')} value={number(unit.canopy.cover * 100)} unit="%" sub={t('growing.kpi.canopySub', { value: number(unit.canopy.crownHa, 2) })} testId="gs-stat-canopy" />
    ),
  ].filter(Boolean);
  const stock = methods.flatMap((m) => {
    const total = unitTotal(unit, m, false);
    const perHa = unitTotal(unit, m, true);
    return total === null
      ? []
      : [<StatCard key={m} label={names.method(m)} value={number(total)} unit={t('growing.units.m3')} sub={perHa === null ? undefined : t('growing.kpi.perHaSub', { value: number(perHa) })} testId={`gs-stat-${m}`} />];
  });
  // G per area by method, its total under it; a method without it (null) has no card.
  const basalCards = Object.values(basal)
    .flat()
    .flatMap((m) => {
      const ha = unitBasal(unit, m);
      const m2 = unitBasal(unit, m, false);
      return ha === null && m2 === null
        ? []
        : [
            <StatCard
              key={m}
              label={t('growing.basal.label', { method: names.method(m) })}
              value={ha === null ? '–' : number(ha, 1)}
              unit={ha === null ? undefined : t('growing.units.m2ha')}
              sub={m2 === null ? undefined : t('growing.kpi.basalSub', { value: number(m2) })}
              testId={`gs-stat-basal-${m}`}
            />,
          ];
    });
  // The areas (with the canopy, a share of them) on one row, the stock by method on the next, G
  // on the last, all on one grid so the cards line up; two to a row on a phone.
  const n = Math.max(areas.length, stock.length, basalCards.length);
  const cols = { base: Math.min(n, 2), md: Math.min(n, 4), lg: Math.min(n, 5) };
  return (
    <Section title={title}>
      <Stack gap="md">
        {areas.length > 0 && (
          <SimpleGrid cols={cols} spacing="md" data-testid="gs-stats-areas">
            {areas}
          </SimpleGrid>
        )}
        {stock.length > 0 && (
          <SimpleGrid cols={cols} spacing="md" data-testid="gs-stats-stock">
            {stock}
          </SimpleGrid>
        )}
        {basalCards.length > 0 && (
          <SimpleGrid cols={cols} spacing="md" data-testid="gs-stats-basal">
            {basalCards}
          </SimpleGrid>
        )}
      </Stack>
    </Section>
  );
}

function ModeSwitch({ mode, onChange }: { mode: Mode; onChange: (mode: Mode) => void }) {
  const { t } = useTranslation('reports');
  return (
    <SegmentedControl
      className="report-actions"
      size="xs"
      style={{ alignSelf: 'flex-start' }}
      value={mode}
      onChange={(v) => onChange(v as Mode)}
      data={[
        { value: 'abs', label: t('growing.mode.abs') },
        { value: 'perHa', label: t('growing.mode.perHa') },
      ]}
    />
  );
}

/** Room kept at a bar's end for its total. */
const TOTAL_WIDTH_PX = 96;
/** A bar's label: narrower on a phone, so the bar keeps most of the row. */
const LABEL_W = { base: 104, sm: 170 };
/** Room for a canopy bar's percentage, right-aligned so the figures line up. */
const CANOPY_VALUE_PX = 56;

/**
 * Horizontal bars, one per method, stacked by genus in the species colours, each total at its end.
 * `scale` puts several charts on one length; `compact` is the small one of a picked unit's card.
 */
function StackChart({ bars, names, unit, scale, compact = false, legend = !compact }: { bars: StackBar[]; names: Names; unit: string; scale?: number; compact?: boolean; legend?: boolean }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  if (!bars.length) {
    return (
      <Text c={brand.textMuted} data-testid="gs-chart-empty">
        {t('growing.chart.empty')}
      </Text>
    );
  }
  const max = scale ?? Math.max(...bars.map((b) => b.total));
  const genera = [...new Set(bars.flatMap((b) => b.segments.map((x) => x.genus)))];
  return (
    <Stack gap="xs" data-testid={compact ? undefined : 'gs-chart'}>
      {bars.map((b) => (
        <Group key={b.method} gap="sm" wrap="nowrap" data-testid={compact ? undefined : `gs-bar-${b.method}`}>
          <Text fz={compact ? 12 : 13} w={compact ? 120 : LABEL_W} style={{ flexShrink: 0 }}>
            {names.method(b.method)}
          </Text>
          <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Box style={{ display: 'flex', height: compact ? 14 : 22, width: `calc((100% - ${TOTAL_WIDTH_PX}px) * ${max > 0 ? b.total / max : 0})` }}>
              {b.segments.map((x) => (
                <Tooltip key={x.genus} label={`${names.genus(x.genus)}: ${number(x.value)} ${unit}`}>
                  <Box data-genus={x.genus} style={{ flexGrow: x.value, flexBasis: 0, background: speciesColor(genusKey(x.genus)) }} />
                </Tooltip>
              ))}
            </Box>
            <Text fz={compact ? 12 : 13} fw={600} pl={6} style={{ whiteSpace: 'nowrap' }} data-testid={compact ? undefined : `gs-total-${b.method}`}>
              {number(b.total)} {unit}
            </Text>
          </Box>
        </Group>
      ))}
      {legend && <GenusLegend genera={genera} names={names} compact={compact} />}
    </Stack>
  );
}

function GenusLegend({ genera, names, compact = false }: { genera: string[]; names: Names; compact?: boolean }) {
  const { t } = useTranslation('reports');
  return (
    <Group gap={compact ? 8 : 'md'} mt={compact ? 2 : 4} style={{ rowGap: compact ? 2 : 6 }} aria-label={t('growing.chart.legend')}>
      {genera.map((g) => (
        <Group key={g} gap={compact ? 4 : 6} wrap="nowrap">
          <Box w={compact ? 8 : 12} h={compact ? 8 : 12} style={{ borderRadius: compact ? 2 : 3, background: speciesColor(genusKey(g)) }} />
          <Text fz={compact ? 11 : 12} c={brand.textMuted}>
            {names.genus(g)}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

/** Every unit of the chosen kind with its totals; a tick (or a click on the row) picks it. */
function UnitsTable({ units, h, methods, basal, perHa, names, picked, onToggle }: { units: Unit[]; h: Hierarchy; methods: string[]; basal: BasalCols; perHa: boolean; names: Names; picked: string[]; onToggle: (id: string) => void }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const unit = perHa ? t('growing.units.m3ha') : t('growing.units.m3');
  // A G column only where some unit has the figure: a method its input lacks everywhere is left out.
  const gOf = (m: string) => (basal[m] ?? []).filter((b) => units.some((u) => u.basalArea[b]));
  const withCanopy = units.some((u) => u.canopy?.cover != null);
  return (
    <ScrollArea.Autosize mah={480}>
      <Table highlightOnHover verticalSpacing="xs" fz="sm" stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="report-actions" w={36} />
            <Table.Th>{t('growing.table.unit')}</Table.Th>
            <Table.Th ta="right">{t('growing.table.area')}</Table.Th>
            {methods.flatMap((m) => [
              <Table.Th key={m} ta="right">{`${names.method(m)} (${unit})`}</Table.Th>,
              ...gOf(m).map((b) => (
                <Table.Th key={`g-${b}`} ta="right" title={t('growing.basal.title')} data-testid={`gs-th-basal-${b}`}>{`${t('growing.basal.label', { method: names.method(b) })} (${t('growing.units.m2ha')})`}</Table.Th>
              )),
            ])}
            {withCanopy && <Table.Th ta="right">{t('growing.table.canopy')}</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {units.map((u) => {
            const on = picked.includes(u.id);
            const path = parentPath(h, u);
            return (
              <Table.Tr key={u.id} data-testid={`gs-unit-${u.id}`} aria-selected={on} onClick={() => onToggle(u.id)} style={{ cursor: 'pointer', background: on ? forest[0] : undefined }}>
                <Table.Td className="report-actions" onClick={(e) => e.stopPropagation()}>
                  <Checkbox aria-label={t('growing.picked.pick', { name: u.name })} checked={on} onChange={() => onToggle(u.id)} data-testid={`gs-pick-${u.id}`} />
                </Table.Td>
                <Table.Td miw={150}>
                  <Text fz="sm" fw={600}>
                    {names.unit(u)}
                  </Text>
                  {path && (
                    <Text fz={11} c={brand.textMuted}>
                      {path}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td ta="right">{u.areaHa === null ? '—' : number(u.areaHa, 2)}</Table.Td>
                {methods.flatMap((m) => {
                  const v = unitTotal(u, m, perHa);
                  return [
                    <Table.Td key={m} ta="right">
                      {v === null ? '—' : number(v)}
                    </Table.Td>,
                    // G is per area either way; the absolute view adds the unit's m² under it.
                    ...gOf(m).map((b) => {
                      const g = unitBasal(u, b);
                      const m2 = unitBasal(u, b, false);
                      return (
                        <Table.Td key={`g-${b}`} ta="right" data-testid={`gs-unit-basal-${u.id}-${b}`}>
                          {g === null ? '—' : number(g, 1)}
                          {!perHa && m2 !== null && (
                            <Text fz={11} c={brand.textMuted}>
                              {`${number(m2)} ${t('growing.units.m2')}`}
                            </Text>
                          )}
                        </Table.Td>
                      );
                    }),
                  ];
                })}
                {withCanopy && <Table.Td ta="right" data-testid={`gs-unit-canopy-${u.id}`}>{u.canopy?.cover == null ? '—' : number(u.canopy.cover * 100)}</Table.Td>}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </ScrollArea.Autosize>
  );
}

/**
 * Canopy cover (zápoj) of the whole area and of each unit of the chosen kind: a bar of the unit's
 * covered area, filled as far as crowns cover it — by genus, in the species colours, when
 * `byGenus` is on, with a table of each genus's share of the whole area. Left out of a summary
 * written before the step measured it.
 */
function CanopySection({ whole, units, level, byGenus, names }: { whole: Unit | null; units: Unit[]; level: string; byGenus: boolean; names: Names }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const rows = [...(whole ? [whole] : []), ...units].filter((u) => u.canopy?.cover != null);
  if (!rows.length) return null;
  // The whole area's genera, largest first, set the order on every bar.
  const ofWhole = whole?.canopy ? canopyShares(whole.canopy) : [];
  const order = ofWhole.map((x) => x.genus);
  const shares = new Map(rows.map((u) => [u.id, canopyShares(u.canopy!, order)]));
  const genera = [...new Set([...order, ...rows.flatMap((u) => shares.get(u.id)!.map((x) => x.genus))])];
  const table = byGenus && ofWhole.length > 0;
  const bars = (
    <Stack gap="sm">
      <ScrollArea.Autosize mah={480}>
        <Stack gap={6}>
          {rows.map((u) => {
            const cover = u.canopy!.cover!;
            const own = u === whole;
            return (
              <Group key={u.id} gap="sm" wrap="nowrap" data-testid={`gs-canopy-${u.id}`} pb={own && rows.length > 1 ? 6 : undefined} style={own && rows.length > 1 ? { borderBottom: `1px solid ${brand.border}` } : undefined}>
                <Text fz={13} w={LABEL_W} fw={own ? 600 : undefined} style={{ flexShrink: 0 }}>
                  {own ? names.level('area') : names.unit(u)}
                </Text>
                <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', height: 18, width: `calc(100% - ${CANOPY_VALUE_PX}px)`, background: brand.surface2, borderRadius: 3, overflow: 'hidden' }}>
                    {byGenus ? (
                      shares.get(u.id)!.map((x) => (
                        <Tooltip key={x.genus} label={`${names.genus(x.genus)}: ${number(x.value * 100, 1)} %`}>
                          <Box data-genus={x.genus} style={{ width: `${x.value * 100}%`, background: speciesColor(genusKey(x.genus)) }} />
                        </Tooltip>
                      ))
                    ) : (
                      <Box style={{ width: `${Math.min(cover, 1) * 100}%`, background: forest[6] }} />
                    )}
                  </Box>
                  <Text fz={13} fw={600} w={CANOPY_VALUE_PX} ta="right" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} data-testid={`gs-canopy-cover-${u.id}`}>
                    {number(cover * 100)} %
                  </Text>
                </Box>
              </Group>
            );
          })}
        </Stack>
      </ScrollArea.Autosize>
      {byGenus && genera.length > 0 && <GenusLegend genera={genera} names={names} />}
    </Stack>
  );
  return (
    <Section title={units.length ? t('growing.sections.canopyOf', { level }) : t('growing.sections.canopy')}>
      <Stack gap="md" data-testid="gs-canopy">
        <Text fz={12} c={brand.textMuted}>
          {t('growing.canopy.note')}
        </Text>
        {/* The genus table beside the bars where there is room, under them on a phone. */}
        <SimpleGrid cols={{ base: 1, lg: table ? 2 : 1 }} spacing="xl">
          {bars}
          {table && (
            <ScrollArea>
              <Table verticalSpacing={4} fz="sm" data-testid="gs-canopy-genera">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('growing.canopy.genus')}</Table.Th>
                    <Table.Th ta="right">{t('growing.canopy.crowns')}</Table.Th>
                    <Table.Th ta="right">{t('growing.canopy.share')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ofWhole.map((x) => (
                    <Table.Tr key={x.genus} data-testid={`gs-canopy-genus-${x.genus}`}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Box w={10} h={10} style={{ borderRadius: 2, background: speciesColor(genusKey(x.genus)), flexShrink: 0 }} />
                          {names.genus(x.genus)}
                        </Group>
                      </Table.Td>
                      <Table.Td ta="right">{number(whole!.canopy!.genera[x.genus] ?? 0, 2)}</Table.Td>
                      <Table.Td ta="right">{`${number(x.value * 100, 1)} %`}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </SimpleGrid>
      </Stack>
    </Section>
  );
}

/** One card per picked unit, their bars on one scale so they compare; `onGoTo`, where there is a map, zooms it to the card's unit. */
function PickedSection({ units, h, methods, basal, perHa, names, onRemove, onGoTo }: { units: Unit[]; h: Hierarchy; methods: string[]; basal: BasalCols; perHa: boolean; names: Names; onRemove: (id: string) => void; onGoTo?: (id: string) => void }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const bars = units.map((u) => stackBars(u, methods, perHa));
  const scale = Math.max(0, ...bars.flat().map((b) => b.total));
  const unit = perHa ? t('growing.units.m3ha') : t('growing.units.m3');
  const ha = t('growing.units.ha');
  return (
    <Section title={t('growing.picked.details')}>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {units.map((u, i) => (
          <Card key={u.id} withBorder radius="lg" padding="md" data-testid={`gs-picked-${u.id}`}>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={0}>
                  <Text fw={700}>{names.unit(u)}</Text>
                  <Text fz={12} c={brand.textMuted}>
                    {parentPath(h, u)}
                  </Text>
                </Stack>
                <Group gap={4} wrap="nowrap" className="report-actions">
                  {onGoTo && (
                    <Button variant="default" size="xs" aria-label={t('growing.picked.goToUnit', { name: u.name })} onClick={() => onGoTo(u.id)} data-testid={`gs-goto-${u.id}`}>
                      {t('growing.picked.goTo')}
                    </Button>
                  )}
                  <CloseButton aria-label={t('growing.picked.remove', { name: u.name })} onClick={() => onRemove(u.id)} />
                </Group>
              </Group>
              <Text fz={13}>
                {[
                  u.areaHa === null ? null : `${t('growing.kpi.area')} ${number(u.areaHa, 2)} ${ha}`,
                  u.coveredHa === null ? null : `${t('growing.kpi.covered')} ${number(u.coveredHa, 2)} ${ha}`,
                  u.orthoCoverage === null ? null : t('growing.kpi.coveredSub', { value: number(u.orthoCoverage * 100) }),
                  u.mlsHa ? `${t('growing.kpi.mls')} ${number(u.mlsHa, 2)} ${ha}` : null,
                  u.fieldHa ? `${t('growing.kpi.field')} ${number(u.fieldHa, 2)} ${ha}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {u.plan && <StandTable plan={u.plan} />}
              {u.stands.length > 0 && <OverlapTable stands={u.stands} />}
              <Table verticalSpacing={2} fz="sm">
                <Table.Tbody>
                  {methods.flatMap((m) => {
                    const v = unitTotal(u, m, perHa);
                    return [
                      v === null ? null : (
                        <Table.Tr key={m}>
                          <Table.Td>{names.method(m)}</Table.Td>
                          <Table.Td ta="right" data-testid={`gs-picked-${u.id}-${m}`}>{`${number(v)} ${unit}`}</Table.Td>
                        </Table.Tr>
                      ),
                      ...(basal[m] ?? []).map((b) => {
                        const g = unitBasal(u, b);
                        const m2 = unitBasal(u, b, false);
                        if (g === null && m2 === null) return null;
                        const text = [g === null ? '–' : `${number(g, 1)} ${t('growing.units.m2ha')}`, ...(!perHa && m2 !== null ? [`${number(m2)} ${t('growing.units.m2')}`] : [])].join(' · ');
                        return (
                          <Table.Tr key={`g-${b}`}>
                            <Table.Td c={brand.textMuted}>{t('growing.basal.label', { method: names.method(b) })}</Table.Td>
                            <Table.Td ta="right" data-testid={`gs-picked-${u.id}-basal-${b}`}>{text}</Table.Td>
                          </Table.Tr>
                        );
                      }),
                    ];
                  })}
                </Table.Tbody>
              </Table>
              <StackChart bars={bars[i]!} names={names} unit={unit} scale={scale} compact />
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Section>
  );
}

/** What the forest plan records of a PSK. */
function StandTable({ plan: p }: { plan: StandPlan }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const rows: [string, string | null][] = [
    ['standId', p.standId],
    ['area', p.areaHa === null ? null : `${number(p.areaHa, 2)} ${t('growing.units.ha')}`],
    ['forestType', p.forestType],
    ['age', p.age === null ? null : number(p.age)],
    ['stocking', p.stocking === null ? null : number(p.stocking, 1)],
    ['composition', p.composition],
    ['description', p.description],
  ];
  return (
    <Table verticalSpacing={4} fz="sm" data-testid="gs-stand">
      <Table.Tbody>
        {rows
          .filter(([, v]) => v !== null)
          .map(([k, v]) => (
            <Table.Tr key={k}>
              <Table.Th w={160}>{t(`growing.stand.${k}`)}</Table.Th>
              <Table.Td>{v}</Table.Td>
            </Table.Tr>
          ))}
      </Table.Tbody>
    </Table>
  );
}

/** The PSKs an MLS plot overlaps, and how much of each lies inside it. */
function OverlapTable({ stands }: { stands: OverlapStand[] }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  return (
    <Table verticalSpacing={2} fz="sm" data-testid="gs-overlaps">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t('growing.overlap.stand')}</Table.Th>
          <Table.Th ta="right">{t('growing.overlap.share')}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {stands.map((s) => (
          <Table.Tr key={s.standId}>
            <Table.Td>{s.name}</Table.Td>
            <Table.Td ta="right">{s.share === null ? '—' : `${number(s.share * 100)} %`}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

const MLS_CHART_HEIGHT = 220;

/** The scan's segments inside the picked units (or all of them): how many are trees, their stock by genus, DBH and height. */
function MlsSection({ picked, stats, loading, failed, scope, names }: { picked: boolean; stats: MlsStats | null; loading: boolean; failed: boolean; scope: string; names: Names }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const m3 = t('growing.units.m3');
  const series = (stats?.genera ?? []).map((g) => ({ name: g.genus, label: names.genus(g.genus), color: speciesColor(genusKey(g.genus)) }));
  const total = stats?.valid.volume ?? 0;
  return (
    <Section title={picked ? `${t('growing.sections.mls')}: ${scope}` : t('growing.sections.mls')}>
      {!picked ? (
        <Text c={brand.textMuted} data-testid="gs-mls-hint">
          {t('growing.mls.hint')}
        </Text>
      ) : loading ? (
        <Center py="md">
          <Loader color="forest" />
        </Center>
      ) : failed || !stats ? (
        <Text c={brand.textMuted} data-testid="gs-mls-unreadable">
          {t('growing.mls.unreadable')}
        </Text>
      ) : (
        <Stack gap="lg" data-testid="gs-mls">
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatCard label={t('growing.mls.segments')} value={number(stats.count)} accent testId="gs-mls-segments" />
            <StatCard label={t('growing.mls.trees')} value={number(stats.valid.count)} sub={t('growing.mls.volumeSub', { value: number(stats.valid.volume) })} testId="gs-mls-trees" />
            <StatCard label={t('growing.mls.invalid')} value={number(stats.invalid.count)} sub={t('growing.mls.volumeSub', { value: number(stats.invalid.volume) })} testId="gs-mls-invalid" />
            <StatCard label={t('growing.mls.volume')} value={number(stats.valid.volume)} unit={m3} />
          </SimpleGrid>
          <Text fz={12} c={brand.textMuted}>
            {t('growing.mls.note')}
          </Text>
          {stats.genera.length > 0 && (
            <>
              <ScrollArea>
                <Table verticalSpacing={4} fz="sm" maw={640} data-testid="gs-mls-genera">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('growing.mls.genus')}</Table.Th>
                      <Table.Th ta="right">{t('growing.mls.count')}</Table.Th>
                      <Table.Th ta="right">{`${t('growing.mls.volume')} (${m3})`}</Table.Th>
                      <Table.Th ta="right">{t('growing.mls.share')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {stats.genera.map((g) => (
                      <Table.Tr key={g.genus} data-testid={`gs-mls-genus-${g.genus}`}>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Box w={10} h={10} style={{ borderRadius: 2, background: speciesColor(genusKey(g.genus)) }} />
                            {names.genus(g.genus)}
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right">{number(g.count)}</Table.Td>
                        <Table.Td ta="right">{number(g.volume, 1)}</Table.Td>
                        <Table.Td ta="right">{total > 0 ? `${number((g.volume / total) * 100)} %` : '—'}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Stack gap={4}>
                  <Text fz={13} fw={600}>
                    {t('growing.mls.dbh')}
                  </Text>
                  <BarChart h={MLS_CHART_HEIGHT} data={stats.dbh} dataKey="bin" type="stacked" series={series} valueFormatter={(v) => number(v)} {...CHART_AXIS_PROPS} gridAxis="y" />
                </Stack>
                <Stack gap={4}>
                  <Text fz={13} fw={600}>
                    {t('growing.mls.height')}
                  </Text>
                  <BarChart h={MLS_CHART_HEIGHT} data={stats.height} dataKey="bin" type="stacked" series={series} valueFormatter={(v) => number(v)} {...CHART_AXIS_PROPS} gridAxis="y" />
                </Stack>
              </SimpleGrid>
              <GenusLegend genera={stats.genera.map((g) => g.genus)} names={names} />
            </>
          )}
        </Stack>
      )}
    </Section>
  );
}

function PlanSection({ gs }: { gs: GrowingStock }) {
  const { t } = useTranslation('reports');
  const header = gs.plan;
  if (!header && !gs.notes.length) return null;
  const headerParts = header
    ? [
        header.source ? t(`growing.footer.source.${header.source}`, { defaultValue: header.source.toUpperCase() }) : null,
        header.name,
        header.lhcCode ? t('growing.footer.lhc', { code: header.lhcCode }) : null,
        header.validFrom !== null && header.validTo !== null ? t('growing.footer.validity', { from: header.validFrom, to: header.validTo }) : null,
        header.author ? t('growing.footer.author', { name: header.author }) : null,
      ].filter((x): x is string => !!x)
    : [];
  return (
    <Section title={t('growing.sections.plan')}>
      <Stack gap="sm">
        {headerParts.length > 0 && (
          <Text fz={13} c={brand.textMuted} data-testid="gs-plan-header">
            {t('growing.footer.plan')}: {headerParts.join(' · ')}
          </Text>
        )}
        {gs.notes.length > 0 && (
          <Stack gap={2} data-testid="gs-notes">
            <Text fz={13} fw={600} c={brand.textMuted}>
              {t('growing.footer.notes')}
            </Text>
            {gs.notes.map((n) => (
              <Text key={n} fz={13} c={brand.textMuted}>
                {n}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Section>
  );
}
